import type { ReactNode } from 'react'
import { cx } from '../controls/cx'
import s from './StatusRail.module.css'

export type StatusRailStepState = 'done' | 'current' | 'upcoming'

export interface StatusRailStep {
  key: string
  label: ReactNode
  state: StatusRailStepState
  /** The glyph while not done. A done step always shows ✓. Defaults to `i + 1`. */
  marker?: ReactNode
  /** The node's tooltip — the HOST's own words for the move it performs. */
  title?: string
  /** Absent ⇒ the node is a `<span>`, not a button. The affordance is a prop. */
  onSelect?: () => void
}

export interface StatusRailProps {
  steps: StatusRailStep[]
  /**
   * Rendered inside the rail, after the last step: the host's trailing
   * controls. The rail supplies the slot's row, its gap and its inset, so a
   * host passes CONTROLS and never a layout — #2406 §1's rule that a domain
   * adapter owns no geometry.
   */
  after?: ReactNode
  className?: string
  testId?: string
}

/**
 * A record's staged position as a compact, CLICKABLE rail.
 *
 * ── why a fourth progress control, measured before it was written ───────────
 * `Steps` is a wizard's chrome: dot beside label, done painted `--good` — and
 * pinned as such by name in `conformance/semanticFillContrast.test.ts`, so not
 * free to change — with no per-node tooltip. `ProcessBar` is deliberately the
 * OPPOSITE of this control: its header rules that "stages are facts, not
 * navigation" and that advancing is a governed verb rather than a click on a
 * node, and it is a 720px-floor scrolling section with 40px nodes.
 * `StageLadder` is a wrapping grid of described tiles whose fourth state,
 * `absent`, is about unknowability rather than outcome.
 *
 * This is the fourth shape and it is a real one: node ABOVE label, a connector
 * running behind the nodes, a 26px node that IS the move, inside a ~500px peek.
 * It is the geometry `apps/console/src/crm/crm.module.css` drew privately.
 *
 * ── it knows no outcome vocabulary, and that is the point ───────────────────
 * A terminal outcome — a pipeline's Won and Lost, a case's Resolved, a tender's
 * Awarded — is NOT a state of this rail, and a `terminal: 'positive' |
 * 'negative'` prop would be CRM's vocabulary in kit clothing.
 *
 * Two reasons, and the first is the decisive one. The rendering consequence is
 * ALREADY expressible: a record sitting on a terminal option has finished every
 * step, which is `state: 'done'` throughout. A fourth state that renders
 * identically to `done` is vocabulary with no consequence — `StageLadder`
 * earned its fourth because `absent` renders differently and carries a required
 * sentence. That is the bar.
 *
 * And `positive | negative` asserts that outcomes come in two polarities. The
 * next host has three: Resolved / Unresolved / Duplicate; Awarded / Withdrawn /
 * No-bid. Either the kit widens an enum for every domain it meets, or a host
 * lies about its own vocabulary.
 *
 * So terminal controls are the host's, rendered into `after` — the same shape
 * `ProcessBar` uses for its governed `advance` ("no `advance` ⇒ no button in
 * the accessibility tree at all — not a disabled one") and `StageLadder` for
 * its `action`.
 *
 * The cost, said out loud: the rail cannot guarantee the slot's rhythm or its
 * pointer target, and there is one slot. A host needing a LEADING terminal
 * needs a `before`, which should be named then rather than by widening `after`.
 */
export function StatusRail({ steps, after, className, testId }: StatusRailProps) {
  return (
    <div className={cx(s.rail, className)} data-testid={testId}>
      {steps.map((step, i) => {
        const mark = step.state === 'done' ? '✓' : step.marker ?? i + 1
        return (
          <div
            key={step.key}
            className={cx(s.step, s[step.state])}
            data-state={step.state}
            aria-current={step.state === 'current' ? 'step' : undefined}
          >
            {step.onSelect
              ? <button type="button" className={s.node} title={step.title} onClick={step.onSelect}>{mark}</button>
              : <span className={s.node}>{mark}</span>}
            <span className={s.label}>{step.label}</span>
          </div>
        )
      })}
      {after != null && <div className={s.after}>{after}</div>}
    </div>
  )
}
